import React from "react";
import PropTypes from "prop-types";
import noop from "lodash/noop";
import useSelect from "./useSelect.js";
import { SelectContext } from "../SelectContext/index.js";
import { ElementSyncProvider } from "../ElementSync/index.js";

/**
 * Implements the state handling necessary to make a custom Select keyboard accessible.
 *
 * It additionally sets up the context necessary for all other Select and Option components
 * work.
 *
 * This component should be the parent wrapper to all custom Select components.
 *
 *
 * #### Select abstraction
 *
 * The select abstraction is accessible via a render prop or via context
 *
 * For example:
 *
 * ```jsx
 * import { SelectContainer } from '@xo-union/ui-select';
 *
 * <SelectContainer>
 *   {(select) => {
 *      // use `select` abstraction
 *   }}
 * </SelectContainer>
 * ```
 *
 * Via context (`MyComponent` must be nested inside of `SelectContainer`)
 *
 * ```jsx
 * import { useSelect } from '@xo-union/ui-select';
 *
 * const MyComponent = () => {
 *   const select = useSelect();
 *   // use `select` abstraction
 * }
 * ```
 *
 * #### Select abstraction properties
 *
 * | Property | Type | Description |
 * |----------|------|-------------|
 * | `select.disabled` | `bool` | True if the `SelectContainer` is disabled |
 * | `select.value` | `string` | Selected value. Corresponds to conotrolled or uncontrolled value  |
 * | `select.isOpen` | `bool` | Open state. Corresponds to controlled or uncontrolled isOpen value |
 * | `select.hasFocusedOption` | `bool` | Whether or not there is a a focused option (via keyboard or after selection) |
 * | `select.activeOption` | `OptionData` | Data of keyboard active option. |
 * | `select.selectedOption` | `OptionData` | Data of selected option. |
 * | `select.options` | `OptionData[]` | Data of selected option. |
 * | `select.close` | `(): void` | Trigger closing of select. |
 * | `select.open` | `(): void` | Trigger openning of select. |
 * | `select.toggle` | `(): void` | Trigger openning of select. |
 * | `select.rotateActiveIndex` | `(offset: number): void` | Offsets the active index. `rotateActiveIndex(1)` moves it one index forward. `rotateActiveIndex(-1)` moves it one index backwards. |
 * | `select.addOption` | `(option: OptionData): void` | Used internally to register options |
 * | `select.removeOption` | `(id: string): void` | Used internally to de-register options. Id corresponds to `OptionContainer` |
 *
 * ### OptionData type
 *
 * | Property | Type | Description |
 * |----------|------|-------------|
 * | `value` | `string` | Value corresponding to `OptionContainer`  |
 * | `id` | `string` | ID corresponding to `OptionContainer` |
 */
const SelectContainer = _ref => {
  let {
    defaultValue,
    value,
    onChange,
    isOpen,
    onOpen,
    onClose,
    disabled,
    children
  } = _ref;
  const select = useSelect({
    isOpen,
    onOpen,
    onClose,
    defaultValue,
    value,
    onChange,
    disabled
  });
  return /*#__PURE__*/React.createElement(SelectContext.Provider, {
    value: select
  }, /*#__PURE__*/React.createElement(ElementSyncProvider, null, typeof children === 'function' ? children(select) : children));
};
process.env.NODE_ENV !== "production" ? SelectContainer.propTypes = {
  /**
   * Initial value in uncontrolled mode
   */
  defaultValue: PropTypes.string,
  /**
   * Controlled value of select
   */
  value: PropTypes.string,
  /**
   * Called when option is selected
   */
  onChange: PropTypes.func,
  /**
   * Controlled opened value
   */
  isOpen: PropTypes.bool,
  /**
   * Called when select is opened
   */
  onOpen: PropTypes.func,
  /**
   * Called when select is closed
   */
  onClose: PropTypes.func,
  /**
   * Set disabled state. Disables all interactive features
   */
  disabled: PropTypes.bool,
  /**
   * A render function or the select's children
   */
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node])
} : void 0;
SelectContainer.defaultProps = {
  onChange: noop,
  onOpen: noop,
  onClose: noop
};
export default SelectContainer;